import React, { useEffect, useState } from 'react'
import Modal from '../../Modal/Modal'
import { EVENT_TYPES } from './calendarEventConfig'

const EMPTY_FORM = { name: '', event_type: 'urlaub', start_date: '', end_date: '' }

function todayStr() {
  return new Date().toISOString().split('T')[0]
}

export default function ClientCalendarEventModal({ isOpen, onClose, onSave, onDelete, initialData }) {
  const isEditing = Boolean(initialData?.id)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (isOpen) {
      setError(null)
      setForm(
        initialData
          ? {
              name: initialData.name,
              event_type: initialData.event_type,
              start_date: initialData.start_date,
              end_date: initialData.end_date,
            }
          : { ...EMPTY_FORM, start_date: todayStr(), end_date: todayStr() },
      )
    }
  }, [isOpen, initialData])

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return }
    if (!form.start_date || !form.end_date) { setError('Both dates are required'); return }
    if (form.end_date < form.start_date) { setError('End date must be on or after start date'); return }
    setSaving(true)
    setError(null)
    try {
      await onSave(form, initialData?.id)
      onClose()
    } catch (e) {
      setError(e?.message || 'Something went wrong')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this event?')) return
    setSaving(true)
    try {
      await onDelete(initialData.id)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header>{isEditing ? 'Edit entry' : 'New entry'}</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          {error && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </p>
          )}

          {/* Name */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-rb-black dark:text-white">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={set('name')}
              placeholder="Event name"
              className="rounded-md border border-rb-widget-border px-3 py-2 text-sm outline-none focus:border-rb-green dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode dark:text-white"
            />
          </div>

          {/* Type */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-rb-black dark:text-white">Type</label>
            <select
              value={form.event_type}
              onChange={set('event_type')}
              className="rounded-md border border-rb-widget-border px-3 py-2 text-sm outline-none focus:border-rb-green dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode dark:text-white"
            >
              {EVENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Dates */}
          <div className="flex gap-4">
            <div className="flex flex-1 flex-col gap-1">
              <label className="text-xs font-semibold text-rb-black dark:text-white">Day from</label>
              <input
                type="date"
                value={form.start_date}
                onChange={set('start_date')}
                className="rounded-md border border-rb-widget-border px-3 py-2 text-sm outline-none focus:border-rb-green dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode dark:text-white"
              />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <label className="text-xs font-semibold text-rb-black dark:text-white">Day to</label>
              <input
                type="date"
                value={form.end_date}
                min={form.start_date}
                onChange={set('end_date')}
                className="rounded-md border border-rb-widget-border px-3 py-2 text-sm outline-none focus:border-rb-green dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode dark:text-white"
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex w-full items-center justify-between">
          {isEditing ? (
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              className="text-sm text-red-500 hover:text-red-700 disabled:opacity-50"
            >
              Delete
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rb-btn-cancel rounded-md px-4 py-2 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-rb-green px-4 py-2 text-sm font-medium text-white hover:bg-rb-green/90 disabled:opacity-50"
            >
              {isEditing ? 'Save' : 'Add New'}
            </button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
